export interface PromoBannerStyle {
  label: string;
  textColor: string;
  buttonBg: string;
  buttonText: string;
  showTitle: boolean;
  showDescription: boolean;
  showButton: boolean;
}

export const PROMO_BANNER_DEFAULT_STYLE: PromoBannerStyle = {
  label: '',
  textColor: '#ffffff',
  buttonBg: '#ffd600',
  buttonText: '#1a1a1a',
  showTitle: true,
  showDescription: true,
  showButton: true
};

const HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const SEPARATOR = '|';

function pickColor(value: any, fallback: string): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  const color = value.trim();

  return HEX_COLOR_PATTERN.test(color) ? color : fallback;
}

/** Anything that is not an explicit `false` counts as visible. */
function pickFlag(value: any): boolean {
  return value !== false;
}

function buildDefaults(fallbackLabel: string): PromoBannerStyle {
  return {
    label: fallbackLabel,
    textColor: PROMO_BANNER_DEFAULT_STYLE.textColor,
    buttonBg: PROMO_BANNER_DEFAULT_STYLE.buttonBg,
    buttonText: PROMO_BANNER_DEFAULT_STYLE.buttonText,
    showTitle: true,
    showDescription: true,
    showButton: true
  };
}

function parseJsonPayload(raw: string, fallbackLabel: string): PromoBannerStyle | null {
  let parsed: any = null;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const defaults = buildDefaults(fallbackLabel);
  const label = typeof parsed.label === 'string' ? parsed.label.trim() : '';

  return {
    label: label || fallbackLabel,
    textColor: pickColor(parsed.textColor, defaults.textColor),
    buttonBg: pickColor(parsed.buttonBg, defaults.buttonBg),
    buttonText: pickColor(parsed.buttonText, defaults.buttonText),
    showTitle: pickFlag(parsed.showTitle),
    showDescription: pickFlag(parsed.showDescription),
    showButton: pickFlag(parsed.showButton)
  };
}

function parseCompactPayload(raw: string, fallbackLabel: string): PromoBannerStyle | null {
  const parts = raw.split(SEPARATOR);

  if (parts.length < 4) {
    return null;
  }


  if (!HEX_COLOR_PATTERN.test(parts[1].trim())) {
    return null;
  }

  const defaults = buildDefaults(fallbackLabel);
  const label = parts[0].trim();


  const flags = parts.length > 4 ? parts[4].trim() : '';

  return {
    label: label || fallbackLabel,
    textColor: pickColor(parts[1], defaults.textColor),
    buttonBg: pickColor(parts[2], defaults.buttonBg),
    buttonText: pickColor(parts[3], defaults.buttonText),
    showTitle: flags.charAt(0) !== '0',
    showDescription: flags.charAt(1) !== '0',
    showButton: flags.charAt(2) !== '0'
  };
}

export function parsePromoBannerStyle(
  addInfo: string | null | undefined,
  fallbackLabel: string = ''
): PromoBannerStyle {
  const defaults = buildDefaults(fallbackLabel);

  if (!addInfo) {
    return defaults;
  }

  const raw = ('' + addInfo).trim();

  if (!raw) {
    return defaults;
  }

  const compact = parseCompactPayload(raw, fallbackLabel);

  if (compact) {
    return compact;
  }

  if (raw.charAt(0) === '{') {
    const fromJson = parseJsonPayload(raw, fallbackLabel);

    if (fromJson) {
      return fromJson;
    }
  }

  const legacy = buildDefaults(fallbackLabel);

  legacy.label = raw || fallbackLabel;

  return legacy;
}

export function serializePromoBannerStyle(style: PromoBannerStyle): string {

  const label = (style.label || '').trim().split(SEPARATOR).join('/');

  const flags =
    (style.showTitle !== false ? '1' : '0') +
    (style.showDescription !== false ? '1' : '0') +
    (style.showButton !== false ? '1' : '0');

  return [
    label,
    pickColor(style.textColor, PROMO_BANNER_DEFAULT_STYLE.textColor),
    pickColor(style.buttonBg, PROMO_BANNER_DEFAULT_STYLE.buttonBg),
    pickColor(style.buttonText, PROMO_BANNER_DEFAULT_STYLE.buttonText),
    flags
  ].join(SEPARATOR);
}
